import { Timestamp } from 'firebase/firestore';
import {
  chain, get,
  groupBy,
  has,
  isDate,
  isEmpty,
  keyBy,
  omit, set,
  sortBy,
} from 'lodash';
import ClinicSettingsUpdatesDB from 'src/fb/clinicSettingsUpdates-db';
import PromptLevelsDB from 'src/fb/promptLevels-db';
import PromptLevelTemplatesDB from 'src/fb/promptLevelTemplates-db';
import { auth as useAuthStore } from 'src/store/auth';
import { network as useNetworkStore } from 'src/store/network';

export default {

  setPromptLevelTemplates(promptLevelTemplates) {
    this.promptLevelTemplates = promptLevelTemplates;
  }, // setPromptLevelTemplates()

  setPromptLevelsLastFetch() {
    this.promptLevelsLastFetchTimestamp = new Date();
  }, // setPromptLevelsLastFetch()

  setPromptLevelTemplateIsDefault({ promptLevelTemplateId, isDefault }) {
    if (has(this.promptLevelTemplates, promptLevelTemplateId)) {
      set(this.promptLevelTemplates, [promptLevelTemplateId, 'isDefault'], isDefault);
    }
  }, // setPromptLevelTemplateIsDefault()

  setPromptLevelTemplate(promptLevelTemplate) {
    this.promptLevelTemplates[promptLevelTemplate.id] = promptLevelTemplate;
  }, // setPromptLevelTemplate()

  setPromptLevelTemplateIsDeleted({ promptLevelTemplateId, isDeleted }) {
    if (has(this.promptLevelTemplates, promptLevelTemplateId)) {
      set(this.promptLevelTemplates, [promptLevelTemplateId, 'isDeleted'], isDeleted);
    }
  }, // setPromptLevelTemplateIsDeleted()

  resetPromptLevelsLastFetch() {
    this.promptLevelsLastFetchTimestamp = null;
  }, // resetPromptLevelsLastFetch()

  /**
   * forceRefresh: Always reload promptLevels
   */
  async loadPromptLevelTemplates(payload) {
    const auth = useAuthStore();

    const clinicId = get(auth, ['userDetails', 'clinic']);
    if (!clinicId) {
      return;
    }

    const promptLevelSettingsLastUpdatedTs = get(
      auth,
      ['clinicSettingsUpdates', 'promptLevelTemplates'],
      null,
    );
    const promptLevelSettingsLastUpdated = (promptLevelSettingsLastUpdatedTs instanceof Timestamp)
      ? promptLevelSettingsLastUpdatedTs.toDate()
      : null;

    const forceRefresh = get(payload, 'forceRefresh', false);

    if (
      !forceRefresh
      && (!isEmpty(this.promptLevelTemplates)) // At least one template exists
      && (isDate(this.promptLevelsLastFetchTimestamp)) // Prompt levels already loaded
      && (!isDate(promptLevelSettingsLastUpdated) // Never changed
        // Or changed after last load
        || (this.promptLevelsLastFetchTimestamp > promptLevelSettingsLastUpdated))
    ) {
      // No need to reload prompt level templates - cached ones are the latest
      return;
    }

    let promptLevelTemplates = [];

    const network = useNetworkStore();

    if (network.offlineMode) {
      const promptLevelTemplatesDb = new PromptLevelTemplatesDB();
      promptLevelTemplates = await promptLevelTemplatesDb.readAll([
        ['clinic', '==', clinicId],
      ]);
      const templateActions = promptLevelTemplates.map(async (template) => {
        const promptLevelsDb = new PromptLevelsDB(template.id);
        const promptLevels = await promptLevelsDb.readAll(null, 'order');
        return {
          ...template,
          promptLevels,
        };
      });
      promptLevelTemplates = await Promise.all(templateActions);
    } else {
      const promptLevelTemplatesDb = new PromptLevelTemplatesDB();
      promptLevelTemplates = await promptLevelTemplatesDb.readAll([
        ['clinic', '==', clinicId],
      ]);

      const promptLevelsDb = new PromptLevelsDB();
      const promptLevelsData = await promptLevelsDb.readByClinic(clinicId);
      const groupedPromptLevels = groupBy(promptLevelsData, 'promptLevelTemplateId');

      promptLevelTemplates = promptLevelTemplates.map((template) => {
        return {
          ...template,
          promptLevels: groupedPromptLevels[template.id],
        };
      });
    }

    this.setPromptLevelTemplates(keyBy(promptLevelTemplates, 'id'));

    this.setPromptLevelsLastFetch();
  }, // loadPromptLevelTemplates()

  async updatePromptLevelTemplate({ promptLevelTemplateId, promptLevelTemplate, promptLevels }) {
    const promptLevelTemplatesDb = new PromptLevelTemplatesDB();
    const clinicId = get(promptLevelTemplate, 'clinic', null);

    if ((clinicId !== null) && promptLevelTemplate.isDefault) {
      const defaultTemplates = await promptLevelTemplatesDb.readAll([
        ['clinic', '==', clinicId],
        ['isDefault', '==', true],
      ]);
      const defaultTemplateActions = defaultTemplates.map((template) => {
        this.setPromptLevelTemplateIsDefault({ promptLevelTemplateId: template.id, isDefault: false });
        return promptLevelTemplatesDb.set({ isDefault: false }, template.id);
      });
      await Promise.all(defaultTemplateActions);
    }

    if (clinicId !== null) {
      // Set last promptLevelTemplate update timestamp for clinic. Setting this before
      // any other changes to make sure we don't set it after changes were actually made.
      // (that could potentially cause updates not to be detected)
      const clinicSettingsUpdatesDb = new ClinicSettingsUpdatesDB();
      await clinicSettingsUpdatesDb.set({ promptLevelTemplates: Timestamp.now() }, clinicId);
    }

    let updatedPromptLevelTemplate;
    if (promptLevelTemplateId) {
      // Update template
      await promptLevelTemplatesDb.set(omit(promptLevelTemplate, ['id']), promptLevelTemplateId);
      updatedPromptLevelTemplate = promptLevelTemplate;
    } else {
      // Create new template
      updatedPromptLevelTemplate = await promptLevelTemplatesDb.create(promptLevelTemplate);
    }

    // Save individual prompt levels
    const promptLevelsDb = new PromptLevelsDB(updatedPromptLevelTemplate.id);
    const savePromptLevelActions = chain(promptLevels)
      .map(async (promptLevel) => {
        if (promptLevel.editStatus === 'delete') {
          const promptLevelObj = {
            ...promptLevel,
            clinic: updatedPromptLevelTemplate.clinic,
            isDeleted: true,
          };
          delete promptLevelObj.editStatus;
          await promptLevelsDb.update(promptLevelObj);
          return promptLevelObj;
        }
        if ((promptLevel.editStatus === 'create') && promptLevel.trialOutcome) {
          const promptLevelObj = {
            ...promptLevel,
            clinic: updatedPromptLevelTemplate.clinic,
          };
          delete promptLevelObj.editStatus;
          const { id } = await promptLevelsDb.create(promptLevelObj);
          promptLevelObj.id = id;

          return promptLevelObj;
        }
        if ((promptLevel.editStatus === 'update') && promptLevel.trialOutcome) {
          const promptLevelObj = {
            ...promptLevel,
            clinic: updatedPromptLevelTemplate.clinic,
          };
          delete promptLevelObj.editStatus;
          await promptLevelsDb.update(promptLevelObj);
          return promptLevelObj;
        }
        return promptLevel;
      })
      .value();
    const updatedPromptLevels = await Promise.all(savePromptLevelActions);

    // Update cached prompt-level templates if a clinic is assigned (not a packaged template)
    if (clinicId !== null) {
      this.setPromptLevelTemplate({
        ...updatedPromptLevelTemplate,
        promptLevels: sortBy(updatedPromptLevels, 'order'),
      });
    }
    return updatedPromptLevelTemplate;
  }, // updatePromptLevelTemplate

  async deletePromptLevelTemplate(promptLevelTemplateId) {
    const promptLevelTemplatesDb = new PromptLevelTemplatesDB();
    await promptLevelTemplatesDb.set({ isDeleted: true }, promptLevelTemplateId);

    this.setPromptLevelTemplateIsDeleted({ promptLevelTemplateId, isDeleted: true });
  }, // deletePromptLevelTemplate()

  setImportPromptLevelMap({ promptLevelTemplateId, promptLevelMap }) {
    this.importPromptLevelMaps[promptLevelTemplateId] = promptLevelMap;
  }, // setImportPromptLevelMap()

  async login(clinicId) {
    await this.loadPromptLevelTemplates(clinicId);
  }, // login()

  logout() {
    this.setPromptLevelTemplates({});
    this.resetPromptLevelsLastFetch();
  }, // logout()

};
